import { useState } from "react";
import {
  useGetPrePumpLearnedPatternQuery,
  useLearnPrePumpPatternNowMutation,
} from "@/store/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { StalenessBadge } from "@/components/domain/StalenessBadge";
import { toast } from "sonner";

/**
 * PrePumpLearnedPatternCard — daily Gemini-Pro study of the top
 * recently-pumped tokens' pre-pump signature. Shows the thesis +
 * common features so the operator can compare against current
 * pre-pump scanner candidates.
 *
 * Cron: bot/intelligence/pre_pump_pattern_learner.py runs every 24h.
 * Manual: "Learn now" button forces a fresh study (~30s, costs $$ Gemini).
 */
export function PrePumpLearnedPatternCard() {
  const [showRaw, setShowRaw] = useState(false);
  const q = useGetPrePumpLearnedPatternQuery(3, { pollingInterval: 300000 });
  const [learnNow, { isLoading: learning }] = useLearnPrePumpPatternNowMutation();

  const patterns: any[] = q.data?.patterns || [];
  const latest = patterns[0];

  async function onLearnNow() {
    if (!confirm("Force-run the pattern study? Calls Gemini Pro on top 5 gainers (~30s, ~$0.10).")) return;
    try {
      const r: any = await learnNow().unwrap();
      const status = r?.status;
      const thesis = r?.gemini?.key_thesis;
      if (status === "ok") {
        toast.success(`Studied ${r.studied_count} pumps — ${(thesis || "").slice(0, 100)}`);
        await q.refetch();
      } else {
        toast(`Status: ${status}`);
      }
    } catch (e: any) {
      toast.error(`learn failed: ${e?.data?.detail ?? "?"}`);
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between gap-2 flex-wrap">
        <CardTitle className="flex items-center gap-2 flex-wrap">
          <span>Pre-pump pattern (learned)</span>
          <StalenessBadge ts={latest?.ts} warnHours={26} staleHours={72} veryStaleHours={168} label="studied" />
          <span className="text-xs text-muted font-normal">
            Gemini Pro studies top recently-pumped tokens, finds the common pre-pump fingerprint
          </span>
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={onLearnNow} disabled={learning}>
          {learning ? "Studying…" : "Learn now"}
        </Button>
      </CardHeader>
      <CardContent>
        {q.isLoading ? (
          <Skeleton className="h-32 w-full" />
        ) : !latest ? (
          <div className="text-sm text-muted">
            No learned patterns yet. Click <em>Learn now</em> to run the first study,
            or wait for the daily cron.
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            {/* Headline */}
            <div className="border border-border rounded p-3 bg-bg2">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Badge tone={
                  (latest.gemini_confidence || 0) >= 70 ? "good"
                  : (latest.gemini_confidence || 0) >= 40 ? "warn"
                  : "muted"
                }>
                  {latest.gemini_confidence ?? 0}% confidence
                </Badge>
                <span className="text-xs text-muted">
                  studied {(latest.tokens || []).length} pumps over last {latest.lookback_days}d
                </span>
              </div>
              <div className="font-medium leading-snug">
                {latest.gemini_thesis || "(no thesis recorded — pattern may have been ambiguous)"}
              </div>
            </div>

            {/* Studied tokens */}
            {Array.isArray(latest.tokens) && latest.tokens.length > 0 && (
              <div>
                <div className="text-xs text-muted mb-1">Studied tokens</div>
                <div className="overflow-x-auto border border-border rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-bg2 text-left text-muted">
                      <tr>
                        <th className="py-1 px-2">Symbol</th>
                        <th className="py-1 px-2 text-right">Biggest day</th>
                        <th className="py-1 px-2 text-right">Lookback return</th>
                        <th className="py-1 px-2 text-right">Pre-pump RSI</th>
                        <th className="py-1 px-2 text-right">Pre-pump drift_14d</th>
                        <th className="py-1 px-2 text-right">Pre-pump vol_d-1</th>
                      </tr>
                    </thead>
                    <tbody>
                      {latest.tokens.map((t: any, i: number) => {
                        const f = t.pre_pump_features || {};
                        return (
                          <tr key={i} className="border-t border-border/40">
                            <td className="py-1 px-2 font-mono font-semibold">{t.symbol}</td>
                            <td className="py-1 px-2 text-right font-mono text-emerald-400">
                              {t.biggest_day_pct != null ? `+${Number(t.biggest_day_pct).toFixed(1)}%` : "—"}
                            </td>
                            <td className="py-1 px-2 text-right font-mono text-emerald-400">
                              {t.return_pct != null ? `+${Number(t.return_pct).toFixed(1)}%` : "—"}
                            </td>
                            <td className="py-1 px-2 text-right font-mono">{f.rsi_daily ?? "—"}</td>
                            <td className="py-1 px-2 text-right font-mono">
                              {f.drift_14d_pct != null ? `${f.drift_14d_pct > 0 ? "+" : ""}${f.drift_14d_pct}%` : "—"}
                            </td>
                            <td className="py-1 px-2 text-right font-mono">
                              {f.vol_m1_vs_14d_avg ?? "—"}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <button
              onClick={() => setShowRaw(!showRaw)}
              className="text-xs text-muted hover:text-fg underline"
            >
              {showRaw ? "Hide" : "Show"} aggregate feature ranges
            </button>
            {showRaw && (
              <pre className="text-xs bg-bg2 border border-border rounded p-2 overflow-x-auto max-h-80">
                {JSON.stringify(latest.features, null, 2)}
              </pre>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
